import React, { useState } from 'react';
import { Col, Container, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { getFormValues } from 'redux-form';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon';
import Api from '../../../util/api';
import useConfig from '../../../util/useConfig';
import { partial } from '../../../util/translation/translation';
import Loading from '../../../shared/components/custom/Loading';
import AlertModal, {
  AlertModalType,
} from '../../../shared/components/custom/AlertModal';
import SeriesForm from './components/SeriesForm';
import SeriesPreview from './components/SeriesPreview';
import SeriesQuestionsPicker, {
  DEFAULT_GUEST_FIELDS,
} from './components/SeriesQuestionsPicker';
import useEventFormOptions from './useEventFormOptions';
import { getApiErrorMessage } from './seriesUtils';
import {
  accessIsMissing,
  compact,
  detailsPayload,
  schedulePayload,
} from './seriesFormValues';

const FORM = 'series_create_form';

const SeriesCreatePage = () => {
  const history = useHistory();
  const p = partial('EventSeries');
  const o = partial('OfferForm');
  const { id: clubId } = useConfig();
  const options = useEventFormOptions();
  const values = useSelector((state) => getFormValues(FORM)(state));
  const [questions, setQuestions] = useState({
    question: [],
    guestFields: DEFAULT_GUEST_FIELDS,
  });
  const [loading, setLoading] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);

  // After the series exists: its questions go to the question service under
  // the series' id, then get copied to the occurrences
  const saveQuestions = async (seriesId) => {
    const { question, guestFields } = questions;
    const asksGuests =
      guestFields.name !== 'off' || guestFields.email !== 'off';
    if (!question.length && !asksGuests) return;
    try {
      await Api.questions.createDomainQuestions(
        {
          domainId: seriesId,
          // Reservations are always the participant's (as on QuestionCard)
          question: question.map(({ askFor, ...rest }) =>
            rest.question_type === 'reservation' || !askFor
              ? rest
              : { ...rest, askFor }
          ),
          guestFields,
        },
        clubId
      );
      await Api.series.syncQuestions(seriesId);
    } catch {
      toast.warning(p('questionsSaveFailed'));
    }
  };

  const onSubmit = async (formValues) => {
    if (accessIsMissing(formValues)) {
      setShowAccessModal(true);
      return;
    }
    const payload = compact({
      ...detailsPayload(formValues, true),
      ...schedulePayload(formValues),
    });
    setLoading(true);
    let created;
    try {
      created = await Api.series.create(payload);
    } catch (err) {
      setLoading(false);
      toast.error(getApiErrorMessage(err));
      return;
    }
    // The series exists from here on, so the rest only warns if it fails
    const image = formValues.imageUrl;
    if (image?.promise) {
      await image.promise('event', 'imageUrl', created.id);
    }
    await saveQuestions(created.id);
    toast.success(
      p('seriesCreated', {
        count: created.occurrenceIds?.length || 0,
        weeks: payload.weeksAhead,
      })
    );
    history.replace(`/events/series/${created.id}`);
  };

  return (
    <Container className="series-page">
      <AlertModal
        visible={showAccessModal}
        modalType={AlertModalType.ERROR}
        handleModal={() => setShowAccessModal(false)}
        message={o('levelOrGroupRequiredMessage')}
      />
      <Loading loading={loading || !options.loaded} />
      <Link to="/events" className="series-page__back">
        <ArrowLeftIcon size={16} /> {p('backToEvents')}
      </Link>
      <h3 className="page-title mb-3">{p('createSeries')}</h3>
      <Row>
        <Col lg={8}>
          <SeriesForm
            form={FORM}
            onSubmit={onSubmit}
            eventCategories={options.eventCategories}
            eventLevels={options.eventLevels}
            eventGroups={options.eventGroups}
            eventTiers={options.eventTiers}
            onCancel={() => history.push('/events')}
          >
            <SeriesQuestionsPicker value={questions} onChange={setQuestions} />
          </SeriesForm>
        </Col>
        <Col lg={4}>
          <div className="series-preview__sticky">
            <SeriesPreview values={values} />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default SeriesCreatePage;
